/**
 * module-routes-config.ts
 *
 * Singleton store for per-module connected routes.
 * Each MFE app (hrms-fe, crm-fe, etc.) can inject its own full set of routes
 * via ERPUIProvider's `moduleRoutes` prop. modules.ts then reads from this
 * store instead of relying on the potentially-stale common-hub pathname files.
 *
 * Usage in consuming app (e.g. hrms-fe main.tsx):
 *   <ERPUIProvider moduleRoutes={{ hrms: Object.values(PathnameHrms) }} ...>
 *
 * Usage in modules.ts:
 *   import { getModuleRoutes } from '../utils/module-routes-config';
 *   const hrmsRoutes = getModuleRoutes('hrms');
 */

type ModuleRoutesMap = Record<string, string[]>;

let moduleRoutes: ModuleRoutesMap = {};

/**
 * Set module routes — called by ERPUIProvider when `moduleRoutes` prop is provided.
 */
export function setModuleRoutes(routes: ModuleRoutesMap): void {
    moduleRoutes = { ...moduleRoutes, ...routes };
}

/**
 * Get all connected routes for a given module key (e.g. 'hrms', 'crm').
 * Returns an empty array if no routes have been registered for that module.
 */
export function getModuleRoutes(moduleKey: string): string[] {
    return moduleRoutes[moduleKey] ?? [];
}

/**
 * Get the full routes map.
 */
export function getAllModuleRoutes(): ModuleRoutesMap {
    return moduleRoutes;
}

/**
 * Reset — primarily for testing.
 */
export function resetModuleRoutes(): void {
    moduleRoutes = {};
}
